import React, {useState} from 'react';
import {Routes, Route, Navigate, useLocation, Link} from 'react-router-dom';
import routes, {type RouteConfig} from '../routes';
import {Menu, X, Server, Settings} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle/ThemeToggle';
import UserDropdown from '../components/UserDropdown/UserDropdown';
import logoWhite from '../assets/img/logos/cway-logo-white-with-name.svg';
import {TokenRefreshIndicator} from '../components/TokenRefreshIndicator';
import {AdminSettings} from '../components/AdminSettings';
import type {LoginInfoQuery} from 'types/__generated__/graphql';

// Temp: LoginInfo still uses old code, will migrate later
import LoginInfo from '../components/LoginInfo/LoginInfo';

/** The signed-in user, as the LoginInfo query selects it. */
type SignedInUser = NonNullable<NonNullable<LoginInfoQuery['loginInfo']>['user']>;

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // LoginInfo hands back `loginInfo.user` from its query once the session is confirmed.
  const [loginInfo, setLoginInfo] = useState<SignedInUser>();
  const location = useLocation();

  const getRoutes = (routes: RouteConfig[]) => {
    return routes
      .filter((route) => route.layout === '/admin')
      .map((route, key) => <Route path={route.path} element={<route.component />} key={key} />);
  };

  const adminRoutes = routes.filter((e) => e.layout === '/admin' && !e.hideInSidebar);

  // Check if user is authenticated
  if (!loginInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking authentication...</p>
        </div>
        <LoginInfo setLoginInfo={setLoginInfo} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      {/* Continuous auth check */}
      <LoginInfo setLoginInfo={setLoginInfo} />

      {/* Header - Full width top bar */}
      <header className="bg-white dark:bg-gray-800 shadow-sm z-30">
        <div className="flex items-center justify-between px-4 py-4">
          {/* Left: Logo and Menu */}
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mr-4"
            >
              <Menu size={24} />
            </button>
            <div className="flex items-center gap-2">
              <img src={logoWhite} alt="Cway Logo" className="h-16 w-auto dark:brightness-100 brightness-0" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Admin</span>
            </div>
          </div>

          {/* Right: Settings, Theme Toggle and User Dropdown */}
          <div className="flex items-center justify-end space-x-4">
            <ThemeToggle />
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Admin Settings"
            >
              <Settings size={20} />
            </button>
            <UserDropdown
              userEmail={loginInfo?.email || 'Loading...'}
              userName={
                loginInfo?.firstName && loginInfo?.lastName ? `${loginInfo.firstName} ${loginInfo.lastName}` : loginInfo?.username || 'Loading...'
              }
              userData={loginInfo}
            />
          </div>
        </div>
      </header>

      {/* Content area with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 dark:bg-gray-950 transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:top-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        >
          <div className="flex flex-col h-full">
            {/* Navigation */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {adminRoutes.map((route, key) => {
                const Icon = route.icon;
                const isActive = location.pathname.includes(route.path);
                return (
                  <Link
                    key={key}
                    to={`/admin${route.path}`}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                      ${isActive ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {Icon && <Icon size={20} className="mr-3" />}
                    {route.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Page content */}
          <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-6">
            <Routes>
              {getRoutes(routes)}
              <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-3 px-6">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500 dark:text-gray-400">© {new Date().getFullYear()} Cway Admin</div>
              <TokenRefreshIndicator />
            </div>
          </footer>
        </div>
      </div>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Admin Settings Dialog */}
      <AdminSettings isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

export default AdminLayout;
